import {openConfirmDialog, openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {
  useDeleteOAuthClientMutation,
  useRegenerateOAuthClientSecretMutation,
} from '@dms/api/security';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  usePermissions,
} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {generateOAuthClientSecret} from './utils/generateOAuthClientSecret';

const OAUTH_CLIENT_GRID_CODE = 'oauth-client';
const PREDEFINED_COLUMNS = ['name'] as const;

export function OAuthClientOverview() {
  const navigate = useNavigate();
  const [deleteOAuthClient] = useDeleteOAuthClientMutation();
  const [regenerateOAuthClientSecret] = useRegenerateOAuthClientSecretMutation();
  const [canCreateOAuthClient] = usePermissions({permissionKeys: ['createOAuthClient']});

  const navigateToDetail = (oauthClientId: string) =>
    navigate(composePath(settingsRoutes.oauthClientManagementDetail, {params: {oauthClientId}}));

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    refreshData,
    getColumnData,
  }) => {
    const oauthClientId = isArray(rowId) ? rowId[0] : rowId;
    const oauthClientName = getColumnData<string>('name')?.value ?? '';

    match(actionKey)
      .with('detail', 'edit', () => navigateToDetail(oauthClientId))
      .with('regenerateSecret', () =>
        openConfirmDialog({
          text: i18n.t('entity.oauthClient.labels.regenerateSecretConfirm'),
          'data-testid': testIds.settings.oauthClientManagement('regenerateSecretConfirm'),
          onConfirm: () =>
            regenerateOAuthClientSecret({
              oauthClientId,
              regenerateOAuthClientSecretRequestBody: {
                clientSecret: generateOAuthClientSecret(),
              },
            })
              .unwrap()
              .then(() => {
                showNotification.success(
                  i18n.t('entity.oauthClient.labels.secretRegeneratedNotification')
                );
                navigateToDetail(oauthClientId);
              })
              .catch(handleApiError),
        })
      )
      .with('delete', () =>
        openDeleteDialog({
          text: i18n.t('entity.oauthClient.labels.deleteDialog', {name: oauthClientName}),
          'data-testid': testIds.settings.oauthClientManagement('deleteDialog'),
          onConfirm: () =>
            deleteOAuthClient({oauthClientId})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <SettingsTemplate
      header={{
        title: i18n.t('entity.oauthClient.labels.oauthClients'),
        subtitle: i18n.t('entity.oauthClient.labels.oauthClientsSubtitle'),
        actions: canCreateOAuthClient
          ? [
              {
                type: 'button',
                title: i18n.t('entity.oauthClient.actions.newOAuthClient'),
                onClick: () => navigate(settingsRoutes.oauthClientManagementCreate),
              },
            ]
          : [],
      }}
      data-testid={testIds.settings.oauthClientManagement('page')}
    >
      <DmsDataGrid
        gridCode={OAUTH_CLIENT_GRID_CODE}
        actionCallback={actionCallback}
        predefinedColumns={PREDEFINED_COLUMNS}
        data-testid={testIds.settings.oauthClientManagement('datagrid')}
      />
    </SettingsTemplate>
  );
}
